'use client';

/**
 * Insets registry — live HTML regions over the baked-in dynamic panels.
 *
 * Each scene file under ./scenes owns exactly one scene and renders absolutely
 * positioned elements in that scene's canvas space (use rectStyle + the
 * SCENE_CANVAS of the scene). Insets sit above the neon layer and above the
 * hotspot hit-map, so a region an inset covers is driven by the inset's own
 * controls; those controls carry `data-hotspot="<id>"` with the same ids the
 * raster hotspots use, so the tour/nudge spotlight still finds them.
 *
 * Every inset must: match the reference panel visually (same background tone,
 * radius, type scale via --sn-unit), keep text legible at 1280×720, animate
 * changes with transform/opacity only, and render nothing that contradicts
 * the artwork around it.
 */

import type { ReferenceSceneProps } from '../../reference/types';
import type { Persona } from '../types';
import ClassroomInsets from './scenes/classroom';
import MotivationInsets from './scenes/motivation';
import TeacherGoalInsets from './scenes/teacher-goal';
import KnowledgeMapInsets from './scenes/knowledge-map';
import PersonalRouteInsets from './scenes/personal-route';
import LearningCycleInsets from './scenes/learning-cycle';
import AgencyInsets from './scenes/agency';
import TeacherWorkspaceInsets from './scenes/teacher-workspace';
import TechnologyInsets from './scenes/technology';
import FinaleInsets from './scenes/finale';

export interface InsetProps extends ReferenceSceneProps {
  persona: Persona;
  /** Hotspot id currently spotlighted (tour/nudge) so an inset control can pulse. */
  spotlight: string | null;
}

export default function SceneInsets(props: InsetProps) {
  switch (props.state.sceneId) {
    case 'classroom': return <ClassroomInsets {...props} />;
    case 'motivation': return <MotivationInsets {...props} />;
    case 'teacher-goal': return <TeacherGoalInsets {...props} />;
    case 'knowledge-map': return <KnowledgeMapInsets {...props} />;
    case 'personal-route': return <PersonalRouteInsets {...props} />;
    case 'learning-cycle': return <LearningCycleInsets {...props} />;
    case 'agency': return <AgencyInsets {...props} />;
    case 'teacher-workspace': return <TeacherWorkspaceInsets {...props} />;
    case 'technology': return <TechnologyInsets {...props} />;
    case 'finale': return <FinaleInsets {...props} />;
  }
}
